import { useState, useEffect } from 'react';
import { Modal } from '../Modal';
import { staffService } from '../../services/api';

const INITIAL = { name: '', email: '', password: '', role: 'staff', slug: '', color: '#2DD4BF', bio: '', is_bookable: true, hourly_cost: '' };

export default function StaffModal({ isOpen, onClose, onSave, person = null }) {
  const isEdit = !!person;
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(person ? { ...INITIAL, ...person, password: '', hourly_cost: person.hourly_cost ?? '' } : INITIAL);
      setErrors({});
    }
  }, [isOpen, person]);

  function change(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  }

  async function submit(e) {
    e.preventDefault();
    const er = {};
    if (!form.name.trim()) er.name = 'Name is required';
    if (!form.email.trim()) er.email = 'Email is required';
    if (!isEdit && !form.password) er.password = 'Set an initial password';
    if (Object.keys(er).length) { setErrors(er); return; }

    setSaving(true);
    const payload = {
      name: form.name, email: form.email, role: form.role, slug: form.slug || form.name,
      color: form.color, bio: form.bio, is_bookable: form.is_bookable,
      hourly_cost: form.hourly_cost === '' ? null : Number(form.hourly_cost)
    };
    if (form.password) payload.password = form.password;
    try {
      const response = isEdit
        ? await staffService.update(person.id, payload)
        : await staffService.create(payload);
      if (response.success) { onSave?.(response.staff); onClose(); }
      else setErrors({ submit: response.error || 'Failed to save' });
    } catch (err) {
      setErrors({ submit: 'An error occurred. Please try again.' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit Person' : 'Add Person'} size="medium">
      <form onSubmit={submit} className="staff-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input id="name" name="name" value={form.name} onChange={change} autoFocus />
            {errors.name && <span className="form-error">{errors.name}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input id="email" name="email" type="email" value={form.email} onChange={change} />
            {errors.email && <span className="form-error">{errors.email}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="slug">Booking link slug</label>
            <input id="slug" name="slug" value={form.slug} onChange={change} placeholder="e.g. leanne" />
          </div>
          <div className="form-group">
            <label htmlFor="role">Role</label>
            <select id="role" name="role" value={form.role} onChange={change}>
              <option value="staff">Staff</option>
              <option value="owner">Owner</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="color">Calendar colour</label>
            <input id="color" name="color" type="color" value={form.color} onChange={change} />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="password">{isEdit ? 'New password (leave blank to keep)' : 'Initial password *'}</label>
          <input id="password" name="password" type="password" value={form.password} onChange={change} autoComplete="new-password" />
          {errors.password && <span className="form-error">{errors.password}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="bio">Bio (shown on the public booking page)</label>
          <textarea id="bio" name="bio" rows="2" value={form.bio} onChange={change} />
        </div>

        <div className="form-group">
          <label htmlFor="hourly_cost">Cost per hour (internal)</label>
          <input id="hourly_cost" name="hourly_cost" type="number" min="0" step="0.5" value={form.hourly_cost} onChange={change} placeholder="e.g. 45" />
          <span className="text-muted" style={{ fontSize: '0.8rem' }}>Wages plus on-costs. Pre-fills labour lines under a job's Costs. Never shown to clients.</span>
        </div>

        <label className="checkbox-label">
          <input type="checkbox" name="is_bookable" checked={!!form.is_bookable} onChange={change} /> Clients can book with this person
        </label>

        {errors.submit && <div className="form-error text-center mb-md mt-md">{errors.submit}</div>}

        <div className="modal-actions">
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save' : 'Add Person')}</button>
        </div>
      </form>
    </Modal>
  );
}
